/**
 * <s-welcome-popup> — opens the welcome discount dialog once per visitor.
 *
 * Attributes:
 *   delay       — seconds to wait before opening (default 4)
 *   storage-key — localStorage key that remembers the visitor saw it
 *
 * Opens immediately when the signup form inside it just succeeded, so the
 * visitor sees their code after the page reloads.
 */
class SWelcomePopup extends HTMLElement {
  #timer = null;
  #ac = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const key = this.getAttribute('storage-key') || 'unsaid-welcome-popup';
    const delay = Number(this.getAttribute('delay') || 4) * 1000;

    dialog.addEventListener('close', () => this.#remember(key), { signal: this.#ac.signal });

    if (dialog.querySelector('[data-signup-success], [role="alert"]')) {
      this.#remember(key);
      if (!dialog.open) dialog.showModal();
      return;
    }

    if (this.#seen(key)) return;

    this.#timer = window.setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog[open]')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #seen(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      /* storage unavailable: popup may show again */
    }
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
